import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { AppError, AUTH_ACTION_TYPE, AuthSelector } from "@obg/ngx.core";
import { of } from "rxjs";
import { catchError, filter, map, switchMap, withLatestFrom } from "rxjs/operators";

import { APP_LIFE_CYCLE_ACTION_TYPE } from "../../app-lifecycle/app-lifecycle.action";
import { CommonState } from "../../state";

import { CUSTOMER_OFFER_ACTION_TYPE, CustomerOfferAction } from "./customer-offer.action";
import { CustomerOfferConfigService } from "./customer-offer.config";
import { CustomerOfferSelector } from "./customer-offer.selector";
import { CustomerOfferService } from "./customer-offer.service";

@Injectable()
export class CustomerOfferEffect {
	setEnabled$ = createEffect(() =>
		this.actions$.pipe(
			ofType(APP_LIFE_CYCLE_ACTION_TYPE.initSuccess),
			map(() => this.config.getOfferStateEnabled()),
			withLatestFrom(this.store.pipe(select(this.selector.isEnabled()))),
			filter(([isEnabled, isEnabledStore]) => isEnabled !== isEnabledStore),
			map(([isEnabled]) => this.action.setEnabled(isEnabled))
		)
	);

	get$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_OFFER_ACTION_TYPE.get),
			withLatestFrom(
				this.store.pipe(select(this.selector.isEnabled())),
				this.store.select(AuthSelector.isAuthenticated)
			),
			switchMap(([, isEnabled, isAuthenticated]) =>
				isEnabled && isAuthenticated
					? this.service.getOfferState().pipe(
							map((offers) => this.action.getSuccess(offers)),
							catchError((error: AppError) => of(this.action.getFail(error)))
						)
					: // async actions should always end up with success or error, to reset `isBusy` state
						of(this.action.getSuccess([]))
			)
		)
	);

	getVisitorOffers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_OFFER_ACTION_TYPE.getVisitorOffers),
			withLatestFrom(this.store.select(AuthSelector.isAuthenticated)),
			filter(([, isAuthenticated]) => !isAuthenticated),
			switchMap(() =>
				this.service.getCampaignOfferState().pipe(
					map((offers) => this.action.getVisitorOffersSuccess(offers)),
					catchError((error: AppError) => of(this.action.getVisitorOffersFail(error)))
				)
			)
		)
	);

	clearVisitorOffers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			withLatestFrom(this.store.pipe(select(this.selector.hasVisitorOffers()))),
			filter(([, hasOffers]) => hasOffers),
			map(() => this.action.clearVisitorOffers())
		)
	);

	clearCustomerOffers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			withLatestFrom(this.store.pipe(select(this.selector.hasOffers()))),
			filter(([, hasOffers]) => hasOffers),
			map(() => this.action.clearCustomerOffers())
		)
	);

	constructor(
		private actions$: Actions,
		private action: CustomerOfferAction,
		private selector: CustomerOfferSelector,
		private service: CustomerOfferService,
		private config: CustomerOfferConfigService,
		private store: Store<CommonState>
	) {}
}
